import { type RouteObject } from 'react-router-dom';
// AdminV2 Pages - Import from feature
import {
  Agent as AgentPage,
  AnalyticsDocs as AnalyticsDocsPage,
  AppointmentRedirect as AppointmentRedirectPage,
  BillingDashboard as BillingDashboardPage,
  BillingDashboardDetail as BillingDashboardDetailPage,
  BillingTasks as BillingTasksPage,
  BillingWorklistRedirect as BillingWorklistRedirectPage,
  BundleDetail as BundleDetailPage,
  BundlesList as BundlesListPage,
  CapabilitiesList as CapabilitiesListPage,
  CapabilityDetail as CapabilityDetailPage,
  ContainerEntitiesList as ContainerEntitiesListPage,
  ContainerEntityDetail as ContainerEntityDetailPage,
  CortexPolicies as CortexPoliciesPage,
  CortexResources as CortexResourcesPage,
  DataMigrationsList as DataMigrationsListPage,
  DemoOrgProvisioning as DemoOrgProvisioningPage,
  EligibilityWorkqueue as EligibilityWorkqueuePage,
  EncounterRedirect as EncounterRedirectPage,
  EncounterTelemetrySessionsList as EncounterTelemetrySessionsListPage,
  EvalResultDetail as EvalResultDetailPage,
  EvalResultsList as EvalResultsListPage,
  EventCatalog as EventCatalogPage,
  FeatureExplorer as FeatureExplorerPage,
  FeedbackDashboard as FeedbackDashboardPage,
  FeedbackSessionsList as FeedbackSessionsListPage,
  FlatResourceRedirect as FlatResourceRedirectPage,
  ImportSessionDetail as ImportSessionDetailPage,
  IntakeHubAdmin as IntakeHubAdminPage,
  McpAccess as McpAccessPage,
  McpTokens as McpTokensPage,
  ModuleEdit as ModuleEditPage,
  ModuleGenerationSandbox as ModuleGenerationSandboxPage,
  MorningWorklist as MorningWorklistPage,
  NoteProcessingSessionDetail as NoteProcessingSessionDetailPage,
  NoteProcessingSessionsList as NoteProcessingSessionsListPage,
  NoteRedirect as NoteRedirectPage,
  NotFound as NotFoundPage,
  OrganizationDetail as OrganizationDetailPage,
  PayerConnections as PayerConnectionsPage,
  PayerSync as PayerSyncPage,
  ProspectDetail as ProspectDetailPage,
  ProspectsList as ProspectsListPage,
  ProviderDetail as ProviderDetailPage,
  ProviderResourceListRedirect,
  Pulses as PulsesPage,
  RcmScoreboard as RcmScoreboardPage,
  ResourceWorkspaceLayout,
  SalesScoreboard as SalesScoreboardPage,
  ScopedResourceCatalog as ScopedResourceCatalogPage,
  ScopedResourceDetail as ScopedResourceDetailPage,
  ScopedResourceList as ScopedResourceListPage,
  Search as SearchPage,
  ShadowNotesList as ShadowNotesListPage,
  StreamingThreadDetail as StreamingThreadDetailPage,
  StreamingThreadsList as StreamingThreadsListPage,
  SystemNotifications as SystemNotificationsPage,
  TemplateConversionOutputViewer as TemplateConversionOutputViewerPage,
  TemplateDetail as TemplateDetailPage,
  TemplateModelConversion as TemplateModelConversionPage,
  TemplatesList as TemplatesListPage,
  TranscriptRedirect as TranscriptRedirectPage,
  VersionComparison as VersionComparisonPage,
} from '@features/admin-v2';
// Clinic Documents Admin Page
import { AdminClinicDocumentsPage } from '@features/clinic-documents';
import { LegacyIntakeStatusRedirect } from '@features/intake-hub/LegacyIntakeStatusRedirect';
import { StaffIntakeStatusPage } from '@features/intake-hub/StaffIntakeStatusPage';
// JotAudit Admin Pages
import {
  AdminJotAuditDashboardPage,
  EncounterAuditDetail,
  EncounterAuditsList,
  NoteAuditDetail,
  ProviderDetail,
  RuleAnalyticsPage,
} from '@features/jotaudit';
// Rules Admin Page
import { AdminRulesManagementPage as RulesManagementPage } from '@features/rules';

import { AdminLayout } from './layouts/AdminLayout';

export const adminV2Routes: RouteObject[] = [
  {
    path: '/admin-v2',
    element: <AdminLayout />,
    children: [
      {
        index: true,
        element: <SearchPage />,
      },
      {
        path: 'provider/:providerId',
        element: <ProviderDetailPage />,
      },
      {
        path: 'provider/:providerId/notes',
        children: [
          {
            index: true,
            element: <ProviderResourceListRedirect resourceType="note" />,
          },
          {
            path: ':noteId',
            element: <NoteRedirectPage />,
          },
        ],
      },
      {
        path: 'provider/:providerId/transcriptions',
        children: [
          {
            index: true,
            element: <ProviderResourceListRedirect resourceType="transcription_session" />,
          },
          {
            path: ':transcriptId',
            element: <TranscriptRedirectPage />,
          },
        ],
      },
      // Encounters and appointments render only through the resource shell
      // now (GDN-2723); these forward to their canonical org/user-scoped URLs.
      {
        path: 'provider/:providerId/encounters',
        children: [
          {
            index: true,
            element: <ProviderResourceListRedirect resourceType="encounter" />,
          },
          {
            path: ':encounterId',
            element: <EncounterRedirectPage />,
          },
        ],
      },
      {
        path: 'provider/:providerId/appointments',
        children: [
          {
            index: true,
            element: <ProviderResourceListRedirect resourceType="appointment" />,
          },
          {
            path: ':appointmentId',
            element: <AppointmentRedirectPage />,
          },
        ],
      },
      {
        path: 'provider/:providerId/resources',
        element: <ProviderResourceListRedirect />,
      },
      {
        path: 'provider/:providerId/note-processing-sessions',
        element: <NoteProcessingSessionsListPage />,
      },
      {
        path: 'provider/:providerId/streaming-threads',
        element: <StreamingThreadsListPage />,
      },
      {
        path: 'provider/:providerId/templates',
        element: <TemplatesListPage />,
      },
      {
        path: 'provider/:providerId/templates/:templateId/:templateVersion',
        element: <TemplateDetailPage />,
      },
      {
        path: 'provider/:providerId/templates/:templateId',
        element: <TemplateDetailPage />,
      },
      {
        path: 'provider/:providerId/templates/:templateId/compare',
        element: <VersionComparisonPage />,
      },
      {
        path: 'provider/:providerId/templates/:templateId/:templateVersion/module-edit',
        element: <ModuleEditPage />,
      },
      {
        path: 'templates/:templateId',
        element: <TemplateDetailPage />,
      },
      {
        path: 'templates/:templateId/compare',
        element: <VersionComparisonPage />,
      },
      {
        path: 'transcript/:id',
        element: <TranscriptRedirectPage />,
      },
      {
        path: 'encounter/:id',
        element: <EncounterRedirectPage />,
      },
      {
        path: 'appointment/:id',
        element: <AppointmentRedirectPage />,
      },
      {
        path: 'note/:noteId',
        element: <NoteRedirectPage />,
      },
      {
        path: 'organization/:organizationId',
        element: <OrganizationDetailPage />,
      },
      {
        path: 'organization/:organizationId/data-migrations',
        element: <DataMigrationsListPage />,
      },
      {
        path: 'organization/:organizationId/data-migrations/session/:sessionId',
        element: <ImportSessionDetailPage />,
      },
      // Worklist + events side by side (GDN-2774) is a section of the org
      // workspace, not a page — rendering it here cost the org rail. The URL
      // it shipped with forwards to that section, query string intact.
      {
        path: 'organization/:organizationId/billing-worklist',
        element: <BillingWorklistRedirectPage />,
      },
      // Canonical resource URLs (GDN-2723). Resources are always org-scoped,
      // so they nest under the organization; a `users/:userId` segment narrows
      // the same views to one member. Both levels share one layout because the
      // level is simply whether that segment is present.
      {
        path: 'organization/:organizationId/resources',
        element: <ResourceWorkspaceLayout />,
        children: [
          {
            index: true,
            element: <ScopedResourceCatalogPage />,
          },
          {
            path: ':resourceType',
            element: <ScopedResourceListPage />,
          },
          {
            path: ':resourceType/:resourceId',
            element: <ScopedResourceDetailPage />,
          },
        ],
      },
      {
        path: 'organization/:organizationId/users/:userId/resources',
        element: <ResourceWorkspaceLayout />,
        children: [
          {
            index: true,
            element: <ScopedResourceCatalogPage />,
          },
          {
            path: ':resourceType',
            element: <ScopedResourceListPage />,
          },
          {
            path: ':resourceType/:resourceId',
            element: <ScopedResourceDetailPage />,
          },
        ],
      },
      // Pre-consolidation flat explorer URLs. Still built by the data-import
      // review browser's committed-resource links (GDN-2723).
      {
        path: 'resources/:resourceType',
        element: <FlatResourceRedirectPage />,
      },
      {
        path: 'resources/:resourceType/:resourceId',
        element: <FlatResourceRedirectPage />,
      },
      {
        path: 'note-processing-sessions',
        element: <NoteProcessingSessionsListPage />,
      },
      {
        path: 'note-processing-session/:npsId',
        element: <NoteProcessingSessionDetailPage />,
      },
      {
        path: 'jotaudit/:organizationId',
        element: <AdminJotAuditDashboardPage />,
      },
      {
        path: 'jotaudit/:organizationId/note/:noteId',
        element: <NoteAuditDetail />,
      },
      {
        path: 'jotaudit/:organizationId/rules/:ruleId/analytics',
        element: <RuleAnalyticsPage />,
      },
      {
        path: 'jotaudit/:organizationId/provider/:userId',
        element: <ProviderDetail />,
      },
      {
        path: 'jotaudit/:organizationId/encounters',
        element: <EncounterAuditsList />,
      },
      {
        path: 'jotaudit/:organizationId/encounter/:encounterId',
        element: <EncounterAuditDetail />,
      },
      {
        path: 'rules',
        element: <RulesManagementPage />,
      },
      { path: 'intake-status', element: <StaffIntakeStatusPage /> },
      {
        path: 'intake-status/:organizationId',
        element: <LegacyIntakeStatusRedirect />,
      },
      {
        path: 'intake-hub',
        element: <IntakeHubAdminPage />,
      },
      {
        path: 'cortex/policies',
        element: <CortexPoliciesPage />,
      },
      {
        path: 'cortex/resources',
        element: <CortexResourcesPage />,
      },
      {
        path: 'event-catalog',
        element: <EventCatalogPage />,
      },
      {
        path: 'feedback',
        element: <FeedbackDashboardPage />,
      },
      {
        path: 'feedback/sessions',
        element: <FeedbackSessionsListPage />,
      },
      {
        path: 'encounter-telemetry-sessions',
        element: <EncounterTelemetrySessionsListPage />,
      },
      {
        path: 'eval-results',
        element: <EvalResultsListPage />,
      },
      {
        path: 'eval-results/:resultId',
        element: <EvalResultDetailPage />,
      },
      {
        path: 'feature-explorer',
        element: <FeatureExplorerPage />,
      },
      {
        path: 'pulses',
        element: <PulsesPage />,
      },
      {
        path: 'system-notifications',
        element: <SystemNotificationsPage />,
      },
      {
        path: 'module-generation-sandbox/:moduleId?',
        element: <ModuleGenerationSandboxPage />,
      },
      {
        path: 'capabilities',
        element: <CapabilitiesListPage />,
      },
      {
        path: 'mcp-access',
        element: <McpAccessPage />,
      },
      {
        path: 'mcp-tokens',
        element: <McpTokensPage />,
      },
      {
        path: 'capability/:capabilityId',
        element: <CapabilityDetailPage />,
      },
      {
        path: 'bundles',
        element: <BundlesListPage />,
      },
      {
        path: 'bundle/:bundleId',
        element: <BundleDetailPage />,
      },
      {
        path: 'streaming-threads',
        element: <StreamingThreadsListPage />,
      },
      {
        path: 'streaming-thread/:threadId',
        element: <StreamingThreadDetailPage />,
      },
      {
        path: 'template-conversion-output-viewer',
        element: <TemplateConversionOutputViewerPage />,
      },
      {
        path: 'template-model-conversion',
        element: <TemplateModelConversionPage />,
      },
      {
        path: 'eligibility-workqueue',
        element: <EligibilityWorkqueuePage />,
      },
      {
        path: 'morning-worklist',
        element: <MorningWorklistPage />,
      },
      {
        path: 'rcm-scoreboard',
        element: <RcmScoreboardPage />,
      },
      {
        path: 'sales-scoreboard',
        element: <SalesScoreboardPage />,
      },
      {
        path: 'billing-dashboard',
        element: <BillingDashboardPage />,
      },
      {
        path: 'billing-dashboard/:organizationId',
        element: <BillingDashboardDetailPage />,
      },
      {
        path: 'billing-tasks',
        element: <BillingTasksPage />,
      },
      {
        path: 'payer-connections',
        element: <PayerConnectionsPage />,
      },
      {
        path: 'prospects',
        element: <ProspectsListPage />,
      },
      {
        path: 'prospect/:prospectSegmentId',
        element: <ProspectDetailPage />,
      },
      {
        path: 'payers/sync',
        element: <PayerSyncPage />,
      },
      {
        path: 'demo-org',
        element: <DemoOrgProvisioningPage />,
      },
      {
        path: 'provider/:providerId/containers/:containerKey',
        element: <ContainerEntitiesListPage />,
      },
      {
        path: 'provider/:providerId/containers/:containerKey/:entityId',
        element: <ContainerEntityDetailPage />,
      },
      {
        path: 'shadow-notes',
        element: <ShadowNotesListPage />,
      },
      {
        path: 'agent',
        element: <AgentPage />,
      },
      {
        path: 'analytics-docs',
        element: <AnalyticsDocsPage />,
      },
      {
        path: 'clinic-documents/:organizationId',
        element: <AdminClinicDocumentsPage />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
];
